// Mesmo fix usado em Inspections.jsx: fotos novas voltam do backend como
// link relativo (ex: "/files/itp_attachments/xxx.jpg"), e o PDF é aberto
// numa janela em branco (sem domínio próprio), então precisa completar
// com o endereço do backend pra imagem carregar.
const BACKEND_ORIGIN = "https://webapp-sitesync.onrender.com";
function resolveAttachmentUrl(url) {
  if (!url) return url;
  return url.startsWith("/") ? `${BACKEND_ORIGIN}${url}` : url;
}

/**
 * ITP inspection → clean, printable HTML report opened in a new window.
 * Uses browser-native print (Ctrl/Cmd + P → Save as PDF).
 */
export function exportInspectionPdf({ inspection, company, breakdown }) {
  const win = window.open("", "_blank", "width=1000,height=1200");
  if (!win) {
    alert("Please allow pop-ups to export the inspection report.");
    return;
  }
  win.document.open();
  win.document.write(buildHtml({ inspection, company, breakdown }));
  win.document.close();
  win.focus();
  win.addEventListener("load", () => {
    // CORRIGIDO: antes imprimia num tempo fixo de 400ms, cedo demais pras
    // fotos terminarem de carregar (principalmente se o backend no Render
    // estiver "dormindo" e demorar pra acordar). Agora espera de verdade
    // cada <img> da página terminar de carregar (ou falhar) antes de
    // chamar o print, com um teto de segurança de 6s pra nunca travar.
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      win.print();
    };

    const imgs = Array.from(win.document.images);
    const pending = imgs.filter((img) => !img.complete);

    if (pending.length === 0) {
      setTimeout(doPrint, 200);
    } else {
      let remaining = pending.length;
      const onSettle = () => {
        remaining -= 1;
        if (remaining <= 0) setTimeout(doPrint, 150);
      };
      pending.forEach((img) => {
        img.addEventListener("load", onSettle, { once: true });
        img.addEventListener("error", onSettle, { once: true });
      });
    }

    // Teto de segurança: garante que o print sempre dispara, mesmo se
    // alguma imagem nunca carregar.
    setTimeout(doPrint, 6000);
  });
}

/**
 * Reconstrói a árvore de itens a partir de parent_result_id (com os mesmos
 * fallbacks usados em Inspections.jsx: parent_id / item_snapshot.parent_id /
 * item_snapshot.parent_item_id) e devolve uma lista plana em ordem de
 * percurso em profundidade (DFS), cada entrada com { node, depth, displayNumber }.
 *
 * Não usa wbs_code para numerar — ele pode estar ausente ou desatualizado.
 * A ordem entre irmãos vem de sort_index, depois sequence_order, depois
 * created_at (mesma prioridade usada no restante do app).
 */
function buildHierarchicalList(results) {
  const byId = {};
  const roots = [];

  const baseSorted = [...results].sort((a, b) => {
    const ai = a.sort_index ?? a.item_snapshot?.sequence_order ?? a.sequence_order ?? 0;
    const bi = b.sort_index ?? b.item_snapshot?.sequence_order ?? b.sequence_order ?? 0;
    if (ai !== bi) return ai - bi;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });

  baseSorted.forEach((r) => { byId[r.id] = { ...r, children: [] }; });

  baseSorted.forEach((r) => {
    const parentId =
      r.parent_result_id ||
      r.parent_id ||
      r.item_snapshot?.parent_id ||
      r.item_snapshot?.parent_item_id;
    if (parentId && byId[parentId]) {
      byId[parentId].children.push(byId[r.id]);
    } else {
      roots.push(byId[r.id]);
    }
  });

  const out = [];
  const walk = (nodes, prefix = "", depth = 0) => {
    nodes.forEach((n, index) => {
      const num = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      out.push({ node: n, depth, displayNumber: num });
      if (n.children.length > 0) walk(n.children, num, depth + 1);
    });
  };
  walk(roots);
  return out;
}

function buildHtml({ inspection, company, breakdown }) {
  const t = inspection.template_snapshot || {};
  const results = inspection.results || [];
  const donut = buildDonutSvg(breakdown);
  const logo = company?.logo_url
    ? `<img class="logo" src="${escapeAttr(company.logo_url)}" alt="${escapeAttr(company.name || "")}"/>`
    : `<div class="logo placeholder"><span>${escapeHtml((company?.name || "S").slice(0, 2).toUpperCase())}</span></div>`;

  const inspectorName = 
    inspection.profiles?.full_name || 
    inspection.profiles?.name || 
    inspection.inspector_name || 
    inspection.inspector_fullname || 
    inspection.created_by_name || 
    inspection.user_name || 
    "SiteSync Admin";

  const locationText = 
    inspection.sites?.address ||
    inspection.site_address ||
    inspection.location_details || 
    inspection.location || 
    inspection.locations?.name || 
    inspection.sites?.name || 
    "—";

  // Description do ITP — não entrava na tabela de metadados antes; só
  // mostra a linha quando o campo tem conteúdo, pra não poluir relatórios
  // de ITPs que não usam essa descrição.
  const descriptionText = inspection.description || "";

  // CORRIGIDO: a numeração/ordem não vem mais de wbs_code (ausente/errado)
  // nem de um contador linear por seção — é recalculada a partir da árvore
  // real (parent_result_id), igual à tela do ITP (Inspections.jsx).
  const hierarchical = buildHierarchicalList(results);

  const itemsHtml = hierarchical.map(({ node: r, depth, displayNumber }) => {
    const snap = r.item_snapshot || {};
    const itemType = String(snap.item_type || snap.type || "").toLowerCase();
    const hasResponse = !!(snap.response_type || snap.checkpoint_type || snap.acceptance_criteria);
    const isSectionHeader =
      itemType.includes("section") ||
      itemType.includes("header") ||
      (!hasResponse && r.children.length > 0 && !snap.response_type);

    if (isSectionHeader) {
      const title = snap.title || snap.activity_description || snap.name || "Section";
      const indent = depth * 20;
      return `<div style="margin: 28px 0 10px ${indent}px; font-size: 13px; font-weight: 700; color: #0f172a; border-bottom: 2px solid #0f172a; padding-bottom: 4px; text-transform: uppercase; letter-spacing: 0.08em;">
        ${escapeHtml(displayNumber)}. — ${escapeHtml(title)}
      </div>`;
    }

    return renderItem(r, displayNumber, depth);
  }).join("");

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>ITP ${escapeHtml(t.title || "Inspection")} — ${escapeHtml(inspection.project_name || "")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; font-family: "IBM Plex Sans", Arial, sans-serif; font-size: 12px; line-height: 1.45; }
  h1, h2, h3 { margin: 0; letter-spacing: -0.01em; }
  .mono { font-family: "IBM Plex Mono", "Menlo", monospace; }
  .overline { font-family: "IBM Plex Mono", monospace; font-size: 9px; letter-spacing: 0.24em; text-transform: uppercase; color: #64748b; }

  header {
    display: flex; align-items: center; gap: 24px; padding-bottom: 16px;
    border-bottom: 2px solid #111;
  }
  .logo { height: 72px; width: auto; max-width: 200px; object-fit: contain; }
  .logo.placeholder { width: 72px; display: inline-flex; align-items: center; justify-content: center; background: #f97316; color: #fff; border-radius: 4px; font-weight: 700; font-size: 22px; }
  header .titleblock { flex: 1; min-width: 0; }
  header h1 { font-size: 22px; font-weight: 600; }
  header .sub { font-size: 12px; color: #475569; margin-top: 4px; }
  header .doc-id { text-align: right; font-family: "IBM Plex Mono", monospace; font-size: 10px; color: #475569; }

  section { margin-top: 20px; page-break-inside: avoid; }
  .meta-table { width: 100%; border-collapse: collapse; }
  .meta-table td { padding: 6px 10px; border: 1px solid #e2e8f0; vertical-align: top; }
  .meta-table td.k { background: #f8fafc; font-family: "IBM Plex Mono", monospace; font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: #475569; width: 22%; }

  .status-summary { display: flex; gap: 24px; align-items: center; padding: 16px; border: 1px solid #e2e8f0; border-radius: 4px; }
  .status-summary svg { flex-shrink: 0; }
  .legend { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; font-family: "IBM Plex Mono", monospace; font-size: 11px; }
  .legend .row { display: flex; align-items: center; gap: 8px; }
  .legend .sw { width: 12px; height: 12px; border-radius: 2px; display: inline-block; }
  .legend .count { margin-left: auto; font-weight: 600; }

  .items { margin-top: 12px; }
  .item {
    border: 1px solid #e2e8f0; border-left-width: 4px; border-radius: 4px;
    padding: 12px 14px; margin-bottom: 10px; page-break-inside: avoid;
  }
  .item.pass { border-left-color: #16a34a; }
  .item.fail { border-left-color: #dc2626; }
  .item.na { border-left-color: #64748b; }
  .item.open { border-left-color: #cbd5e1; }
  .item.subitem { border-left-width: 3px; }
  .item .head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
  .badge {
    display: inline-block; padding: 4px 8px; border-radius: 3px; border: 1px solid #cbd5e1;
    font-family: "IBM Plex Mono", monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #475569;
  }
  .badge.number { font-weight: 700; background: #f1f5f9; color: #0f172a; border-color: #cbd5e1; padding: 4px 10px; font-size: 11px; min-width: 50px; text-align: center; }
  .badge.hold { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .badge.witness { background: #fefce8; color: #a16207; border-color: #fde68a; }
  .badge.review { background: #fff7ed; color: #c2410c; border-color: #fed7aa; }
  .badge.pass { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
  .badge.fail { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .badge.na { background: #f1f5f9; color: #475569; border-color: #cbd5e1; }
  .item .activity { font-weight: 600; margin-top: 2px; }
  .item .criteria { color: #475569; font-size: 11px; margin-top: 2px; }
  .kv { margin-top: 8px; font-family: "IBM Plex Mono", monospace; font-size: 11px; color: #1f2937; }
  .kv strong { color: #0f172a; letter-spacing: 0.1em; font-size: 10px; text-transform: uppercase; margin-right: 6px; }
  .comments { margin-top: 6px; background: #f8fafc; border-left: 3px solid #cbd5e1; padding: 8px 10px; font-size: 11px; }
  .comments.negative { background: #fef2f2; border-color: #dc2626; }
  .attachments { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
  .attachments img { height: 80px; width: 80px; object-fit: cover; border: 1px solid #e2e8f0; border-radius: 3px; }
  .sig { display: block; max-width: 260px; max-height: 96px; margin-top: 6px; border: 1px solid #e2e8f0; border-radius: 3px; background: #fff; }
  .inspector { color: #475569; font-size: 10px; font-family: "IBM Plex Mono", monospace; margin-top: 4px; }
  .ncr-flag { display: inline-block; margin-top: 4px; padding: 2px 6px; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; border-radius: 3px; font-family: "IBM Plex Mono", monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; }

  footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-family: "IBM Plex Mono", monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #64748b; }
  .print-hint { position: fixed; top: 8px; right: 8px; padding: 6px 10px; background: #f97316; color: #fff; border-radius: 3px; font-family: "IBM Plex Mono", monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 0.16em; }
  @media print { .print-hint { display: none; } }
</style>
</head>
<body>
  <div class="print-hint">Use ⌘/Ctrl + P → Save as PDF</div>
  <header>
    ${logo}
    <div class="titleblock">
      <div class="overline">Inspection & Test Plan Report</div>
      <h1>${escapeHtml(t.title || inspection.title || "Inspection")}</h1>
      <div class="sub"><strong>${escapeHtml(inspection.project_name || "—")}</strong> · <strong>Location:</strong> ${escapeHtml(locationText)} · Rev ${escapeHtml(String(t.revision ?? inspection.revision ?? "—"))} · ${escapeHtml(t.spec_section || inspection.spec_section || "no spec section")}</div>
    </div>
    <div class="doc-id">
      <div>${escapeHtml(company?.name || "")}</div>
      <div>Doc ID: ${escapeHtml((inspection.id || "").slice(0, 8).toUpperCase())}</div>
      <div>Printed ${new Date().toLocaleString()}</div>
    </div>
  </header>

  <section>
    <div class="overline">Project metadata</div>
    <table class="meta-table" style="margin-top:8px">
      <tr>
        <td class="k">Project</td><td>${escapeHtml(inspection.project_name || "—")}</td>
        <td class="k">Location</td><td>${escapeHtml(locationText)}</td>
      </tr>
      <tr>
        <td class="k">Inspector</td><td>${escapeHtml(inspectorName)}</td>
        <td class="k">Status</td><td>${escapeHtml(inspection.status || "—")}</td>
      </tr>
      <tr>
        <td class="k">Created</td><td class="mono">${escapeHtml(inspection.created_at ? new Date(inspection.created_at).toLocaleString() : "—")}</td>
        <td class="k">Updated</td><td class="mono">${escapeHtml(inspection.updated_at ? new Date(inspection.updated_at).toLocaleString() : "—")}</td>
      </tr>
      ${descriptionText ? `<tr>
        <td class="k">Description</td><td colspan="3">${escapeHtml(descriptionText)}</td>
      </tr>` : ""}
    </table>
  </section>

  <section>
    <div class="overline">Inspection item status</div>
    <div class="status-summary" style="margin-top:8px">
      ${donut}
      <div class="legend">
        <div class="row"><span class="sw" style="background:#16a34a"></span> Conforming / Pass <span class="count">${breakdown.pass}</span></div>
        <div class="row"><span class="sw" style="background:#dc2626"></span> Deficient / Fail <span class="count">${breakdown.fail}</span></div>
        <div class="row"><span class="sw" style="background:#64748b"></span> N/A <span class="count">${breakdown.na}</span></div>
        <div class="row"><span class="sw" style="background:#cbd5e1"></span> Uninspected <span class="count">${breakdown.open}</span></div>
        <div class="row" style="grid-column: 1 / -1; padding-top: 4px; border-top: 1px solid #e2e8f0; margin-top: 4px"><strong>${breakdown.inspected}/${breakdown.total}</strong> inspected</div>
      </div>
    </div>
  </section>

  <section class="items">
    <div class="overline">Inspection items</div>
    ${itemsHtml || '<div style="margin-top:8px; color:#64748b">No items on this inspection.</div>'}
  </section>

  <footer>
    <span>${escapeHtml(company?.name || "SiteSync")} · ${escapeHtml(inspection.project_name || "")}</span>
    <span>Generated by SiteSync · ${escapeHtml((inspection.id || "").slice(0, 8))}</span>
  </footer>
</body></html>`;
}

function renderItem(r, displayNum, depth = 0) {
  const snap = r.item_snapshot || {};
  const sel = r.status_selection;
  // CORRIGIDO: item com algum valor preenchido (foto, texto, assinatura,
  // número, data) contava errado como "open" (pendente). Itens assim são
  // considerados "feitos" — mesma regra usada no gráfico (computeBreakdown
  // em Inspections.jsx conta foto/texto/assinatura como respondido).
  const filled = hasValue(r);
  let cls = "open";
  if (sel === "Pass" || sel === "Yes") cls = "pass";
  else if (sel === "Fail" || sel === "No") cls = "fail";
  else if (sel === "N/A") cls = "na";
  else if (filled) cls = "pass";

  const cpKey = (snap.checkpoint_type || "").split(" ")[0].toLowerCase();
  const valueLine = renderResponseValue(r);
  const negative = sel === "Fail" || sel === "No";
  // CORRIGIDO: fotos novas vêm do backend como link relativo (ex:
  // "/files/itp_attachments/xxx.jpg"), não como endereço completo. Sem
  // resolver com o domínio do backend, o PDF tenta carregar a imagem a
  // partir do próprio documento em branco e sempre falha (ícone quebrado).
  // Mesmo fix já aplicado em Inspections.jsx.
  const attachments = (r.attachments || []).map(
    (a) => `<img src="${escapeAttr(resolveAttachmentUrl(a.url))}" alt="attachment"/>`,
  ).join("");
  
  const sigPayload = r.signature_data || (r.text_value && r.text_value.startsWith("data:image") ? r.text_value : "");
  const sig = sigPayload
    ? `<img class="sig" src="${escapeAttr(sigPayload)}" alt="signature"/>`
    : "";

  // CORRIGIDO: antes só mostrava "Inspected by..." quando inspected_at
  // vinha preenchido pelo backend — o que não acontece ao só subir uma
  // foto. Agora, se o item tem conteúdo (foto/texto/assinatura/etc) mas
  // sem esse timestamp, mostra "Inspected" em vez de "Not yet inspected".
  const inspector = r.inspected_at
    ? `Inspected by ${escapeHtml(r.inspected_by_name || r.inspector_name || "—")} · ${escapeHtml(new Date(r.inspected_at).toLocaleString())}`
    : (sel || filled)
      ? "Inspected"
      : "Not yet inspected";
  const ncr = r.ncr_id ? `<div class="ncr-flag">NCR raised</div>` : "";
  
  // CORRIGIDO: a indentação agora reflete a profundidade real na árvore
  // (parent_result_id), não apenas "tem ponto no wbs_code ou não".
  const subitemClass = depth > 0 ? "subitem" : "";
  const marginLeft = depth * 20;

  return `<div class="item ${cls} ${subitemClass}" style="margin-left:${marginLeft}px">
    <div class="head">
      <span class="badge number">${escapeHtml(displayNum)}</span>
      ${snap.checkpoint_type ? `<span class="badge ${cpKey}">${escapeHtml(snap.checkpoint_type)}</span>` : ""}
      ${snap.response_type ? `<span class="badge">${escapeHtml(snap.response_type)}</span>` : ""}
      ${sel ? `<span class="badge ${sel === "Pass" || sel === "Yes" ? "pass" : sel === "Fail" || sel === "No" ? "fail" : "na"}">${escapeHtml(sel)}</span>` : ""}
    </div>
    <div class="activity">${escapeHtml(snap.activity_description || snap.title || "")}</div>
    ${snap.acceptance_criteria ? `<div class="criteria"><strong>Acceptance:</strong> ${escapeHtml(snap.acceptance_criteria)}</div>` : ""}
    ${valueLine}
    ${sig}
    ${attachments ? `<div class="attachments">${attachments}</div>` : ""}
    ${r.comments ? `<div class="comments ${negative ? "negative" : ""}"><strong>Comments</strong> — ${escapeHtml(r.comments)}</div>` : ""}
    <div class="inspector">${inspector} ${ncr}</div>
  </div>`;
}

function renderResponseValue(r) {
  const rt = r.item_snapshot?.response_type;
  if (rt === "Number" && r.numeric_value !== null && r.numeric_value !== undefined) {
    return `<div class="kv"><strong>Value</strong> ${escapeHtml(String(r.numeric_value))}</div>`;
  }
  if (rt === "Date" && r.date_value) {
    return `<div class="kv"><strong>Date</strong> ${escapeHtml(r.date_value)}</div>`;
  }
  if (rt === "Text" && r.text_value && !r.text_value.startsWith("data:image")) {
    return `<div class="kv"><strong>Notes</strong> ${escapeHtml(r.text_value)}</div>`;
  }
  return "";
}

function hasValue(r) {
  const rt = r.item_snapshot?.response_type;
  if (rt === "Date") return !!r.date_value;
  if (rt === "Number") return r.numeric_value !== null && r.numeric_value !== undefined;
  if (rt === "Text") return !!r.text_value && !r.text_value.startsWith("data:image");
  if (rt === "Signature") return !!r.signature_data || (!!r.text_value && r.text_value.startsWith("data:image"));
  // CORRIGIDO: antes só reconhecia os nomes antigos "PHOTOS"/"Upload Photos".
  // O tipo usado hoje no app é "Photo" (singular) — por isso todo item de
  // foto aparecia sempre como "Not yet inspected" no PDF, mesmo com foto
  // anexada. Agora checa a presença de anexos direto, sem depender do
  // texto exato do response_type.
  if (Array.isArray(r.attachments) && r.attachments.length > 0) return true;
  return false;
}

function buildDonutSvg(b) {
  const size = 140;
  const radius = 56;
  const inner = 34;
  const cx = size / 2;
  const cy = size / 2;
  const total = b.total || 1;
  // REMOVIDO o bucket "entered": o objeto breakdown calculado em
  // Inspections.jsx não tem esse campo (só total/inspected/pass/fail/na/open)
  // — por isso aparecia "Entered / Pending: undefined" no relatório.
  const buckets = [
    { key: "pass", value: b.pass, color: "#16a34a" },
    { key: "fail", value: b.fail, color: "#dc2626" },
    { key: "na", value: b.na, color: "#64748b" },
    { key: "open", value: b.open, color: "#cbd5e1" },
  ];
  let angle = -Math.PI / 2;
  const arcs = buckets.filter((x) => x.value > 0).map((x) => {
    const frac = x.value / total;
    const end = angle + frac * Math.PI * 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + radius * Math.cos(angle);
    const y1 = cy + radius * Math.sin(angle);
    const x2 = cx + radius * Math.cos(end);
    const y2 = cy + radius * Math.sin(end);
    const xi2 = cx + inner * Math.cos(end);
    const yi2 = cy + inner * Math.sin(end);
    const xi1 = cx + inner * Math.cos(angle);
    const yi1 = cy + inner * Math.sin(angle);
    const d = [
      `M ${x1} ${y1}`, `A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`,
      `L ${xi2} ${yi2}`, `A ${inner} ${inner} 0 ${large} 0 ${xi1} ${yi1}`, "Z",
    ].join(" ");
    angle = end;
    return `<path d="${d}" fill="${x.color}"/>`;
  }).join("");
  const emptyRing = b.total === 0
    ? `<circle cx="${cx}" cy="${cy}" r="${(radius + inner) / 2}" fill="none" stroke="#cbd5e1" stroke-width="${radius - inner}"/>`
    : "";
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    ${emptyRing}
    ${arcs}
    <text x="${cx}" y="${cy - 2}" text-anchor="middle" style="font-family:'IBM Plex Mono',monospace;font-size:16px;font-weight:600;fill:#0f172a">${b.inspected}/${b.total}</text>
    <text x="${cx}" y="${cy + 14}" text-anchor="middle" style="font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:2px;text-transform:uppercase;fill:#64748b">Inspected</text>
  </svg>`;
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

function escapeAttr(s) { 
  return escapeHtml(s); 
}
